import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, X, ShoppingCart, Plus, Minus, Trash2,
  ChevronLeft, ChevronRight, Barcode, Printer, CheckCircle, Loader2,
  CreditCard, Smartphone, Wallet, Banknote, Users, Percent, DollarSign,
  User, FileText, RefreshCw, CalendarX, CalendarPlus, Lock
} from 'lucide-react';
import toast from 'react-hot-toast';
import Cart from '../components/pos/Cart';
import ProductSearch from '../components/pos/ProductSearch';
import CustomerSelector from '../components/pos/CustomerSelector';
import QuickProductAdd from '../components/pos/QuickProductAdd';
import SaleUnitSelector from '../components/pos/SaleUnitSelector';
import PaymentPanel from '../components/pos/PaymentPanel';
import Receipt from '../components/pos/Receipt';
import { printReceipt, saveReceiptPdf } from '../utils/receiptPdf';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SaleEndModal from '../components/layout/SaleEndModal';
import StartNewDayModal from '../components/layout/StartNewDayModal';
import { getAvailableStock } from '../utils/formatStock';
import { usePosCart } from '../hooks/usePosCart';
import { usePosCheckout } from '../hooks/usePosCheckout';
import { fetchProducts } from '../redux/slices/productSlice';
import { fetchCustomers, createCustomer } from '../redux/slices/customerSlice';
import { fetchTodayStatus } from '../redux/slices/shiftSlice';
import { fetchShopInfo } from '../redux/slices/settingSlice';

const categories = ['All', 'Pan Masala', 'Gutka', 'Supari', 'Mouth Freshener', 'Cigarettes', 'Beverages', 'Snacks', 'Other'];

const loadPosProducts = () => fetchProducts({ all: true, isActive: true });

export default function POS() {
  const dispatch = useDispatch();
  const { products, loading: productLoading } = useSelector((state) => state.products);
  const { customers, loading: customerLoading } = useSelector((state) => state.customers);
  const { user } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.settings);
  const shiftStatus = useSelector((state) => state.shift.status);

  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  const [unitSelectorProduct, setUnitSelectorProduct] = useState(null);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showSaleEnd, setShowSaleEnd] = useState(false);
  const [showStartNewDay, setShowStartNewDay] = useState(false);
  const barcodeInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const shopInfo = useSelector((state) => state.settings?.shopInfo);
  const taxRate = settings?.taxRate || 0;
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';
  const dayClosed = shiftStatus !== 'OPEN';

  const openUnitSelector = useCallback((product) => {
    setUnitSelectorProduct(product);
    setShowUnitSelector(true);
  }, []);

  const {
    cartItems, cartPieces, discount, setDiscount, subtotal, total,
    addToCart, addWithUnit, updateQuantity, removeItem, clearCart, findStockViolation,
  } = usePosCart({ products, dayClosed, taxRate, onUnitRequired: openUnitSelector });

  const { checkout, loading: paymentLoading } = usePosCheckout({
    cartItems,
    cartPieces,
    subtotal,
    discount,
    taxRate,
    customer,
    notes,
    products,
    findStockViolation,
  });


  useEffect(() => {
    dispatch(loadPosProducts());
    dispatch(fetchCustomers({ page: 1, limit: 200 }));
    dispatch(fetchTodayStatus());
    dispatch(fetchShopInfo());
  }, [dispatch]);

  const filteredProducts = useMemo(() => {
    // Deleted / deactivated products must never reach the till.
    let result = products.filter((p) => p.active !== false && p.isActive !== false);
    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }
    return result;
  }, [products, activeCategory]);

  const handleClearCart = useCallback(() => {
    clearCart();
    setNotes('');
    setCustomer(null);
    toast.success('Cart cleared');
  }, [clearCart]);

  const handleBarcodeSearch = useCallback(
    (barcode) => {
      const needle = String(barcode || '').trim().toLowerCase();
      const product = products.find((p) => {
        if (p.active === false || p.isActive === false) return false;
        return String(p.barcode || '').toLowerCase() === needle
          || String(p.serialNumber || '').toLowerCase() === needle;
      });
      if (product) {
        addToCart(product);
      } else {
        toast.error(`No product found with barcode: ${barcode}`);
        searchInputRef.current?.focus();
      }
    },
    [products, addToCart]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (cartItems.length > 0) setShowPayment(true);
      } else if (e.key === 'Escape') {
        setShowPayment(false);
        setShowReceipt(false);
        setShowQuickAdd(false);
      }
    },
    [cartItems.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCheckout = async (paymentData) => {
    const outcome = await checkout(paymentData);
    if (!outcome.ok) {
      if (outcome.reason === 'insufficient-stock') setShowPayment(false);
      return;
    }

    setLastTransaction({
      ...outcome.sale,
      id: outcome.sale?.transactionId || outcome.sale?.saleId,
      date: outcome.sale?.createdAt || new Date(),
      items: cartItems,
      subtotal,
      discount: parseFloat(discount) || 0,
      tax: (subtotal - (parseFloat(discount) || 0)) * (taxRate / 100),
      total,
      paymentMethod: paymentData.method,
      amountTendered: outcome.sale?.amountPaid ?? paymentData.amountTendered,
      change: outcome.sale?.changeAmount ?? paymentData.change,
      shortfall: outcome.sale?.paymentShortfall ?? paymentData.shortfall,
    });
    setShowPayment(false);
    setShowReceipt(true);
    setNotes('');
    setCustomer(null);
    clearCart();
    dispatch(loadPosProducts());
  };

  const handleCustomerCreate = async (data) => {
    const result = await dispatch(createCustomer(data)).unwrap();
    const created = result.customer || result;
    setCustomer(created);
    toast.success('Customer created');
    return created;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-[calc(100vh-64px)] flex flex-col"
    >
      <div className="hidden">
        <input
          ref={barcodeInputRef}
          type="text"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleBarcodeSearch(e.target.value);
              e.target.value = '';
            }
          }}
          autoComplete="off"
        />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 overflow-hidden">
        <div className="flex-1 flex flex-col gap-4 min-h-0">
          {dayClosed && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400"
            >
              <Lock size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold">Shop is closed – sales are blocked</p>
                <p className="text-xs mt-0.5">
                  The business day has been ended. Press{' '}
                  <span className="font-semibold">START NEW DAY</span> to open the shop again and start selling.
                </p>
              </div>
            </motion.div>
          )}

          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <ProductSearch
                products={products}
                onAddToCart={addToCart}
                onBarcodeSearch={handleBarcodeSearch}
                inputRef={searchInputRef}
              />
            </div>
            {dayClosed ? (
              <Button
                variant="success"
                size="md"
                icon={CalendarPlus}
                onClick={() => {
                  if (isAdmin) {
                    setShowStartNewDay(true);
                  } else {
                    toast('START NEW DAY is available only to the admin/owner. Please contact the owner to open the shop.', {
                      icon: '🔒',
                    });
                  }
                }}
                title="Open the shop for a new business day"
              >
                START NEW DAY
              </Button>
            ) : (
              <Button
                variant={isAdmin ? 'danger' : 'secondary'}
                size="md"
                icon={CalendarX}
                onClick={() => {
                  if (isAdmin) {
                    setShowSaleEnd(true);
                  } else {
                    toast('SALE END is available only to the admin/owner. Please contact the owner to end today\'s sale.', {
                      icon: '🔒',
                    });
                  }
                }}
                title="Close today's business session"
              >
                SALE END
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              icon={Plus}
              onClick={() => {
                if (dayClosed) {
                  toast.error("Today's session is closed. Sales are not allowed.");
                  return;
                }
                setShowQuickAdd(true);
              }}
            >
              Quick Add
            </Button>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
            {categories.map((cat) => (
              <motion.button
                key={cat}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-sm font-medium rounded-xl whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {cat}
              </motion.button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {productLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner size="lg" message="Loading products..." />
              </div>
            ) : filteredProducts.length === 0 ? (
              <EmptyState
                icon={Package}
                title="No products found"
                description={'No products in this category'}
                actionLabel="Add Quick Product"
                onAction={() => setShowQuickAdd(true)}
              />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
                {filteredProducts.map((product, idx) => {
                  const stockLeft = getAvailableStock(product);
                  const outOfStock = stockLeft <= 0;
                  return (
                    <motion.button
                      key={product._id || product.id || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      whileHover={outOfStock ? {} : { y: -3, scale: 1.02 }}
                      whileTap={outOfStock ? {} : { scale: 0.98 }}
                      onClick={() => !outOfStock && addToCart(product)}
                      disabled={outOfStock}
                      className={`relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-left transition-all ${
                        outOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3 mx-auto">
                        {product.image ? (
                          <img src={product.image} alt={product.productName || product.name} className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <Package size={24} className="text-gray-400" />
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white text-center truncate mb-1">
                        {product.productName || product.name}
                      </h4>
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400 text-center">
                        Rs. {(product.salePrice || product.price || 0).toLocaleString()}
                      </p>
                      <p className={`text-xs text-center mt-1 ${
                        outOfStock ? 'text-red-500' : stockLeft <= 5 ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {outOfStock ? 'Out of Stock' : `${stockLeft} pcs`}
                      </p>
                      {outOfStock && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 rounded-xl flex items-center justify-center">
                          <span className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">Out of Stock</span>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="w-full lg:w-[420px] xl:w-[480px] flex flex-col">
          <Cart
            items={cartItems}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onClearCart={handleClearCart}
            discount={discount}
            onDiscountChange={setDiscount}
            taxRate={taxRate}
            customer={customer}
            onCustomerChange={() => setShowCustomerSelect(true)}
            notes={notes}
            onNotesChange={setNotes}
            onCheckout={() => {
              if (cartItems.length > 0) setShowPayment(true);
            }}
          />
        </div>
      </div>

      <Modal isOpen={showPayment} onClose={() => setShowPayment(false)} size="md" title="Payment">
        <PaymentPanel
          totalAmount={total}
          onConfirm={handleCheckout}
          onCancel={() => setShowPayment(false)}
          loading={paymentLoading}
          customer={customer}
          customers={customers}
          customerLoading={customerLoading}
          onCustomerSelect={(c) => setCustomer(c)}
          onCustomerClear={() => setCustomer(null)}
          onCreateCustomer={handleCustomerCreate}
        />
      </Modal>

      <Modal isOpen={showReceipt} onClose={() => setShowReceipt(false)} size="sm" showCloseButton={false}>
        <Receipt
          transaction={lastTransaction}
          shopName={shopInfo?.name}
          shopAddress={shopInfo?.address}
          shopPhone={shopInfo?.phone}
          onPrint={() => {
            try {
              printReceipt(lastTransaction, shopInfo);
            } catch (err) {
              toast.error(err?.message || 'Could not open print dialog');
            }
          }}
          onEmail={() => {
            toast.info('Email receipt is not set up yet');
          }}
          onDownload={() => {
            try {
              saveReceiptPdf(lastTransaction, shopInfo);
              toast.success('Receipt saved');
            } catch (err) {
              toast.error(err?.message || 'Could not save receipt');
            }
          }}
          onClose={() => setShowReceipt(false)}
        />
      </Modal>

      <Modal isOpen={showQuickAdd} onClose={() => setShowQuickAdd(false)} size="sm" title="Quick Product Add">
        <QuickProductAdd
          onAdd={(product) => {
            addToCart(product);
            setShowQuickAdd(false);
          }}
          onCancel={() => setShowQuickAdd(false)}
        />
      </Modal>

      <Modal isOpen={showCustomerSelect} onClose={() => setShowCustomerSelect(false)} size="md" title="Select Customer">
        <CustomerSelector
          customers={customers}
          selectedCustomer={customer}
          onSelect={(c) => {
            setCustomer(c);
            setShowCustomerSelect(false);
          }}
          onClear={() => {
            setCustomer(null);
          }}
          onCreateNew={(data) => {
            handleCustomerCreate({ fullName: data?.search || '', phoneNumber: '' }).catch((err) => {
              toast.error(err?.message || 'Failed to create customer');
            });
            setShowCustomerSelect(false);
          }}
          loading={customerLoading}
          variant="full"
        />
      </Modal>

      <Modal isOpen={showUnitSelector} onClose={() => { setShowUnitSelector(false); setUnitSelectorProduct(null); }} size="sm" title="Select Unit">
        {unitSelectorProduct && (
          <SaleUnitSelector
            product={unitSelectorProduct}
            onConfirm={({ unit, quantity, price }) => {
              addWithUnit(unitSelectorProduct, { unit, quantity, price });
              setShowUnitSelector(false);
              setUnitSelectorProduct(null);
            }}
            onCancel={() => { setShowUnitSelector(false); setUnitSelectorProduct(null); }}
          />
        )}
      </Modal>

      <SaleEndModal isOpen={showSaleEnd} onClose={() => setShowSaleEnd(false)} />
      <StartNewDayModal isOpen={showStartNewDay} onClose={() => setShowStartNewDay(false)} />
    </motion.div>
  );
}
